import {useCallback, useRef, useState} from 'react';
import type {ReactFlowInstance} from 'reactflow';

import * as analytics from 'analytics/Analytics';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {FlowchartSidepanelManagerContextValue} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {
  SidepanelChange,
  SidepanelType,
} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';

import {AddNodeOption, GetAddNodeOptionsArgs, getAddNodeOptions} from '../AddNodeDropdown';
import {buildControlFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticTaskSchemaNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {ApplyFlowchartChangesFn, UseFlowchartStateParams} from './FlowchartStateTypes';
import {getExecutionNodeTypeIdentifierForNode} from './useFlowchartStateAnalytics';

interface UseAddNodeFromRightHandleParams {
  addNodeParams: UseFlowchartStateParams['addNodeParams'];
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  flowchartTypeName: string;
  editorMode: FlowchartEditorMode;
  openSidepanelForNode: (nodeId: string) => void;
  openSidepanelForNodeWithField: (nodeId: string, fieldId: string, rowId: string) => void;
  sidepanelManager: FlowchartSidepanelManagerContextValue;
}

export interface AddNodeFromRightHandleResult {
  getAddNodeOptionsForRightHandle: (
    sourceNodeId: string,
    reactFlowInstance: ReactFlowInstance
  ) => Array<AddNodeOption> | null;
  placeholderNodeIdBeingAdded: string | null;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
}

/**
 * Manages adding a new node from a source node's right handle (button).
 * Produces the option-list builder passed into the interactive flowchart, and
 * materializes the node + connecting edge on the canvas via the sidepanel manager.
 * Tracks placeholder node additions so callers can open their sidepanel once
 * they're mounted.
 */
export function useAddNodeFromRightHandle({
  addNodeParams,
  applyFlowchartChanges,
  flowchartTypeName,
  editorMode,
  openSidepanelForNode,
  openSidepanelForNodeWithField,
  sidepanelManager,
}: UseAddNodeFromRightHandleParams): AddNodeFromRightHandleResult {
  const [placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded] = useState<string | null>(null);

  // Prevents duplicate materialization when the right-handle action fires more than once
  // before the first add completes (e.g. repeated click handling or overlapping async onSelect calls).
  const materializingFromSourceNodeIdsRef = useRef<Set<string>>(new Set());
  const getAddNodeOptionsForRightHandleRef = useRef<
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance) => Array<AddNodeOption> | null
  >(() => null);

  const materializeNewNodeFromRightHandle = useCallback(
    (sourceNodeId: string, newNode: FlowchartNode, analyticsCallback: () => void) => {
      if (materializingFromSourceNodeIdsRef.current.has(sourceNodeId)) {
        return;
      }
      materializingFromSourceNodeIdsRef.current.add(sourceNodeId);

      const isPlaceholder = isInteractivePlaceholderNode(newNode);
      const edge = buildControlFlowEdge({
        sourceNodeId,
        targetNodeId: newNode.id,
      });

      const sidepanelChange: SidepanelChange = isPlaceholder
        ? {
            sidepanel: SidepanelType.NODE_SETTINGS,
            nodeId: newNode.id,
          }
        : {sidepanel: null};

      const isExecutionMode =
        addNodeParams.editorMode === FlowchartEditorMode.ACM_EDITOR ||
        addNodeParams.editorMode === FlowchartEditorMode.PCM_EDITOR;

      const executionModeParams = isExecutionMode ? addNodeParams : null;

      sidepanelManager
        .changeSidepanel({
          sidepanelChange,
          onBeforeSidepanelChanged: async () => {
            await applyFlowchartChanges(
              (flowchartConfig) => {
                const nodeChanges: Array<FlowchartNodeChange> = [
                  {
                    type: 'add',
                    item: {
                      ...newNode,
                      data: {
                        ...newNode.data,
                        getAddNodeOptionsForRightHandle: getAddNodeOptionsForRightHandleRef.current,
                      },
                    },
                  },
                ];
                const edgeChanges = [
                  {
                    type: 'add' as const,
                    item: edge,
                  },
                ];

                // In execution mode, restore any initial transitions for this source→target
                // pair that were removed from the draft state (e.g. by orphan cleanup after
                // a prior add+delete cycle). Including them in the same change batch ensures
                // the edge-addition machinery finds and promotes the original transition
                // (with field mappings) instead of creating a blank new one.
                if (executionModeParams != null && isInteractiveTaskNode(newNode)) {
                  const sourceNodeData = executionModeParams.nodesStaticData.find(
                    (n) => n.id === sourceNodeId
                  );
                  if (sourceNodeData != null && isStaticTaskSchemaNodeData(sourceNodeData)) {
                    const sourcePTDVId = sourceNodeData.taskDefinitionVersionSpec.clientId;
                    const targetPTDVId = newNode.data.taskDefinitionVersionSpec.clientId;
                    const transitionsToRestore = Object.values(executionModeParams.allowedTransitions).filter(
                      (t) =>
                        t.sourcePTDVIds.includes(sourcePTDVId) &&
                        t.targetPTDVId === targetPTDVId &&
                        flowchartConfig.metadata.transitions[t.id] == null
                    );
                    if (transitionsToRestore.length > 0) {
                      return {
                        nodeChanges,
                        edgeChanges,
                        transitionChanges: transitionsToRestore.map((t) => ({
                          type: 'add' as const,
                          transition: {...t, isPlanned: true},
                        })),
                      };
                    }
                  }
                }

                return {nodeChanges, edgeChanges};
              },
              false,
              isPlaceholder
            );

            if (isPlaceholder) {
              setPlaceholderNodeBeingAdded(newNode.id);
            }
          },
        })
        .finally(() => {
          if (materializingFromSourceNodeIdsRef.current.has(sourceNodeId)) {
            materializingFromSourceNodeIdsRef.current.delete(sourceNodeId);
          }
        });

      analyticsCallback();
    },
    [addNodeParams, applyFlowchartChanges, sidepanelManager]
  );

  const materializeNewNodeFromRightHandleRef = useRef(materializeNewNodeFromRightHandle);
  materializeNewNodeFromRightHandleRef.current = materializeNewNodeFromRightHandle;

  const getAddNodeOptionsForRightHandle = useCallback(
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance): Array<AddNodeOption> | null => {
      const baseArgs = {
        flowchartEditorConfigurator: addNodeParams.flowchartEditorConfigurator,
        sourceNodeId,
        reactFlowInstance,
        singletonTaskSchemasByName: addNodeParams.singletonTaskSchemasByName,
        canAddExistingTaskSchema: addNodeParams.taskDefinitionsForSidepanel.some(
          (def) => !isFlowchartTaskSchema(def.latestVersion.processTaskSchema)
        ),
        rootTaskSchema: addNodeParams.rootTaskSchema,
        editorMode,
        openSidepanelForNode,
        openSidepanelForNodeWithField,
        onNodeChangesOnly: (
          nodeChangeGenerator: (flowchartConfig: FlowchartConfigWithMetadata) => Array<FlowchartNodeChange>
        ) => {
          applyFlowchartChanges((flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
            edgeChanges: [],
          }));
        },
        setSourceNodeAndNewNodeBeingAdded: ([originNodeId, newNode]: [string, FlowchartNode]) => {
          materializeNewNodeFromRightHandleRef.current(originNodeId, newNode, () => {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_ADDED_FROM_RIGHT_HANDLE_CLICK, {
              actionMethod: ActionMethod.BUTTON_CLICK,
              actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
              nodeType: getExecutionNodeTypeIdentifierForNode(newNode),
              flowchartType: flowchartTypeName,
              editorMode,
            });
          });
        },
        didInitialFlowchartHaveChainedRouters: addNodeParams.didInitialFlowchartHaveChainedRouters,
      };

      const addNodeEditorMode = addNodeParams.editorMode;
      let args: GetAddNodeOptionsArgs;
      if (
        addNodeEditorMode === FlowchartEditorMode.ACM_EDITOR ||
        addNodeEditorMode === FlowchartEditorMode.PCM_EDITOR
      ) {
        args = {
          ...baseArgs,
          editorMode: addNodeEditorMode,
          allowedTransitions: addNodeParams.allowedTransitions,
          nodesStaticData: addNodeParams.nodesStaticData,
          taskDefinitionsForSidepanel: addNodeParams.taskDefinitionsForSidepanel,
        };
      } else {
        args = {
          ...baseArgs,
          editorMode: addNodeEditorMode,
        };
      }

      return getAddNodeOptions(args);
    },
    [
      addNodeParams,
      editorMode,
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      applyFlowchartChanges,
      flowchartTypeName,
    ]
  );

  getAddNodeOptionsForRightHandleRef.current = getAddNodeOptionsForRightHandle;

  return {
    getAddNodeOptionsForRightHandle,
    placeholderNodeIdBeingAdded,
    setPlaceholderNodeBeingAdded,
  };
}
